"use client";

// imports
import Cookies from 'js-cookie';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect, useCallback } from 'react';

// lib files
import { APP_SIGN_IN_FEATURE_CATEGORY, STATION_ID_COOKIE } from '@/lib/constants';
import { APP_SIGN_IN_METRICS } from '@/lib/metric-strings';
import { changeStationModalAtom } from '@/lib/atoms/change-station-modal';
import { clientMetricLogger } from '@/lib/logger-client';
import {
  defaultAppSignInState,
  appSignInAtom,
  PageStatusEnum,
} from "@/lib/atoms/app-sign-in";
import { getBestStationWhiteLogo } from "@/lib/helpers/get-best-station-white-logo";
import { getCampaignInfoCode } from "@/lib/helpers/get-campaign-info-code";
import { LocalizationServiceV3StationData } from '@/lib/types/api/stations-data';
import { openChangeStationModal } from '@/lib/helpers/open-change-station-modal';
import { putCampaignInfo, setFavoriteStation } from '@/lib/profile';
import { registerToUser } from '@/app/(app-sign-in)/tv/helpers';
import { sendPbsGtmEvent } from "@/lib/helpers/send-pbs-gtm-event";
import { setStationCookie } from '@/lib/helpers/set-station-cookie';
import { signinModalAtom, SignInModalStateEnum } from "@/lib/atoms/signin-modal";
import { useHasMounted } from '@/lib/hooks';
import { useIdentity } from "@/lib/hooks/useIdentity";

// components
import AppSignInError from "@/components/app-sign-in/AppSignInError/AppSignInError";
import AppSignInSuccess from "@/components/app-sign-in/AppSignInSuccess/AppSignInSuccess";
import AuthCodeInput from "@/components/app-sign-in/AuthCodeInput/AuthCodeInput";
import ChangeStationModal from '@/components/Localization/ChangeStationModal/ChangeStationModal';
import ConfirmEmail from "@/components/app-sign-in/ConfirmEmail/ConfirmEmail";
import FlowHeader from '@/components/FlowHeader/FlowHeader';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';
import SignInModal from '@/components/SignInModal/SignInModal';

// types
import { StationData } from "@/lib/types/api/stations-data";

// Helpers
import { checkDeviceType } from "./helpers";

// styles
import styles from './AppSignInFlow.module.scss';

interface AppSignInFlowProps {
  stationData?: StationData;
}

// A note on Analytics:
// We are using the standard PBS GTM event structure, which uses the abstracted
// sendPbsGtmEvent() function to send events to GTM.
// We use APP_SIGN_IN_FEATURE_CATEGORY as the feature category for all events.

/**
 * AppSignInFlow
 * The orchestration component for the app sign in flow.
 * @param {AppSignInFlowProps} props - currently just StationData
 * @returns {JSX.Element}
*/
export default function AppSignInFlow(props: AppSignInFlowProps) {
  const { stationData } = props;
  const hasMounted = useHasMounted();
  const { cachedIdentity, isSignedIn, isResolving: isLoadingProfile } = useIdentity();
  const [appSignInState, setAppSignInState] = useAtom(appSignInAtom);
  const setSigninModalOpen = useSetAtom(signinModalAtom)
  const setChangeStationModalOpen = useSetAtom(changeStationModalAtom);

  // If the user refreshes (or otherwise remounts) while sitting on the success
  // view, reset the flow so they see the authCodeInput view on the fresh load.
  // This runs only on mount.
  useEffect(() => {
    if (appSignInState.resetOnNextLoad) {
      setAppSignInState({ ...defaultAppSignInState });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // If the user is already on the success view, a late profile poll
    // response shouldn't re-drive the flow — bail out so we don't clobber
    // the success state and bounce the user back through the flow.
    if (appSignInState.pageStatus === PageStatusEnum.success) {
      return;
    }
    // Update state if we have a user profile
    if (cachedIdentity?.stations[0]) {
      const profileFavoriteStation = cachedIdentity.stations[0] || null;

      let stationLogoToUse = null;
      let stationShortCommonNameToUse = null;
      const isPassportMember = cachedIdentity.isPassport || false;

      const userHasFavoriteStationAndIsLocalizedToIt =
        profileFavoriteStation &&
        stationData &&
        stationData.attributes.call_sign.toUpperCase() === profileFavoriteStation.toUpperCase();

      if (userHasFavoriteStationAndIsLocalizedToIt) {
        stationLogoToUse = getBestStationWhiteLogo(stationData.attributes.images) || null;
        stationShortCommonNameToUse = stationData.attributes.short_common_name;
      }

      setAppSignInState((prevState) => ({
        ...prevState,
        favoriteStation: profileFavoriteStation,
        isPassportMember: isPassportMember,
        stationLogo: stationLogoToUse,
        stationShortCommonName: stationShortCommonNameToUse,
        uid: cachedIdentity.uid,
      }));
    } else if(cachedIdentity) {
      // Possible case in staging is that a user has no stations, but _is_ signed
      // in, we need to advance the step with the UID, so that we can move them
      // to the station chooser.
      setAppSignInState((prevState) => ({
        ...prevState,
        uid: cachedIdentity.uid,
      }));
    }
  // Run this only if we have a profile
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedIdentity]);

  const determineDeviceType = useCallback(async () => {
    const authCode = appSignInState.authCode!

    try {
      clientMetricLogger(
        APP_SIGN_IN_METRICS.NAMESPACE,
        APP_SIGN_IN_METRICS.ATTEMPT,
      );
      const typeResponse = await checkDeviceType(authCode)
      switch(true) {
        case typeResponse?.error !== undefined:
          sendPbsGtmEvent({
            feature_category: APP_SIGN_IN_FEATURE_CATEGORY,
            field_entry_value: appSignInState.authCode,
            feature_name: "sign in code check",
            object_action: "code error",
          });
          clientMetricLogger(
            APP_SIGN_IN_METRICS.NAMESPACE,
            APP_SIGN_IN_METRICS.FAILURE_CODE_ERROR,
          );
          return setAppSignInState((prevState) => ({
            ...prevState,
            authCodeError: typeResponse.error,
            pageStatus: PageStatusEnum.authCodeInput
          }))
        case typeResponse?.expired === true:
          sendPbsGtmEvent({
            feature_category: APP_SIGN_IN_FEATURE_CATEGORY,
            field_entry_value: appSignInState.authCode,
            feature_name: "sign in code check",
            object_action: "code expired",
          });
          clientMetricLogger(
            APP_SIGN_IN_METRICS.NAMESPACE,
            APP_SIGN_IN_METRICS.FAILURE_CODE_EXPIRED,
          );
          return setAppSignInState((prevState) => ({
            ...prevState,
            authCodeError: 'The code you entered has expired. Please request a new code in your application.',
            pageStatus: PageStatusEnum.authCodeInput
          }))
        case !typeResponse.expired && typeResponse.deviceType !== undefined:
          // Holding a profile is enough to confirm an email against. The state
          // this worked around (unreadable pbs_uid, valid session, status stuck
          // at NotLoaded forever) no longer exists: the cookie is not consulted
          // and every path reaches a terminal state.
          return setAppSignInState((prevState) => ({
            ...prevState,
            deviceType: typeResponse.deviceType,
            pageStatus: isSignedIn
              ? PageStatusEnum.confirmEmail
              : PageStatusEnum.signIn,
          }))
      }
    } catch (_error) {
      return setAppSignInState((prevState) => ({
        ...prevState,
        authCodeError: 'There was a network error, please try again later.',
        pageStatus: PageStatusEnum.authCodeInput
      }))
    }
  }, [appSignInState, setAppSignInState, isSignedIn]);

  // Helper function for moving the user from the choose station step
  // to device registration. It updates state with station information based
  // on how this function is invoked.
  const moveFromChooseStationToDeviceRegister =
    useCallback((
      passThrough: boolean = false,
      station?: LocalizationServiceV3StationData,
    ) => {
    // making double plus sure this is set
    const currentStationID = Cookies.get(STATION_ID_COOKIE);

    switch (true) {
      // 1. Happy Path - we just move the user through
      case passThrough:
        setAppSignInState((prevState) => ({
          ...prevState,
          pageStatus: PageStatusEnum.deviceRegisterLoading,
        }));
        break;
      // 2. The user is localized, that has been used as a favorite station,
      // and we end up here.
      case station == undefined && stationData !== undefined && currentStationID !== undefined:
        const stationLogoToUse = getBestStationWhiteLogo(stationData.attributes.images) || null;

        setAppSignInState((prevState) => ({
          ...prevState,
          stationShortCommonName: stationData.attributes.short_common_name,
          favoriteStation: stationData.attributes.call_sign,
          stationLogo: stationLogoToUse,
          pageStatus: PageStatusEnum.deviceRegisterLoading,
        }));
        break;
      // 3. The user has no favorite station, isn't localized,
      // they've chosen a station with the ChangeStationModal, and confirmed
      // which gives us the `station` object that we use for the UI
      case station !== undefined:
        const { callsign, common_name_short } = station;

        setAppSignInState((prevState) => ({
          ...prevState,
          stationShortCommonName: common_name_short,
          favoriteStation: callsign,
          pageStatus: PageStatusEnum.deviceRegisterLoading,
        }));
        break;
      // If the user somehow gets here, let's show an error
      default:
        setAppSignInState((prevState) => ({
          ...prevState,
          pageStatus: PageStatusEnum.error,
        }));
        break;
    }
  }, [setAppSignInState, stationData]);

  // Helper function for when we save a favorite station,
  // which then moves us to moveFromChooseStationToDeviceRegister
  const saveFavoriteStation = useCallback((
    stationId: string,
    // This optional `station` parameter is *only* sent after a user chooses a station
    // with the ChangeStationModal. See handleConfirmStationClick(). That happens only
    // when the user isn't localized (i.e. outside the US).
    // So, it's _absence_ is also an important piece of information -
    // that tells us the user was localized already, and we use the localized data
    // (i.e. stationData) instead inside moveFromChooseStationToDeviceRegister().
    station?: LocalizationServiceV3StationData,
  ) => {
    setFavoriteStation(stationId).then(() => {
      moveFromChooseStationToDeviceRegister(false, station);
    }).catch(err => {
      setAppSignInState((prevState) => ({
        ...prevState,
        pageStatus: PageStatusEnum.error,
      }))
      console.error(err);
    });
  }, [moveFromChooseStationToDeviceRegister, setAppSignInState]);

  // Custom handler function for the ChangeStationModal.
  const handleConfirmStationClick = (station: LocalizationServiceV3StationData) => {
    const { station_id: stationId } = station;
    // set the station cookie
    setStationCookie(station);
    // close the modal
    setChangeStationModalOpen(false);
    // save the favorite station
    saveFavoriteStation(stationId, station);
  }

  // The Flow runs like this:
  // 1. User enters an auth code
  // 2. If the user is signed in, confirm their email address.
  // 3. If they are not signed in, show the sign in modal.
  // 4. Does the user have a favorite station?
  // 5. Register the device
  // 6. Much success
  useEffect(() => {
    switch(appSignInState.pageStatus) {
      case PageStatusEnum.authCodeInput:
        const params = new URLSearchParams(window.location.search);
        const authCodeQueryParam = params.get("authCode");
        const sourceQueryParam = params.get("source");
        if (hasMounted && (authCodeQueryParam || sourceQueryParam)) {
          // if either query param is set, send an event indicating that there was an authcode and/or the source
          sendPbsGtmEvent({
            feature_category: APP_SIGN_IN_FEATURE_CATEGORY,
            feature_name: "loaded with params",
            object_action: `user loaded ${authCodeQueryParam ? `with` : `without`} authCode query param${sourceQueryParam ? `, from ${sourceQueryParam}` : ''}`,
          });
        }
        break;
      case PageStatusEnum.authCodeSubmitLoading:
        determineDeviceType()
        break;
      case PageStatusEnum.confirmEmail:
        // if the user gets to the confirm email step, but opts to use a different email address, we log them out and
        // bring them back to /tv/ while still in this state. So, if there is no profile in this case, we instead
        // send them to sign in, which will trigger the modal, etc.
        if (!isSignedIn && !isLoadingProfile) {
          setAppSignInState((prevState) => ({
            ...prevState,
            pageStatus: PageStatusEnum.signIn
          }))
        }
        break;
      case PageStatusEnum.signIn:
        // If the user has come back from SSO signed in, advance to chooseStation
        if (isSignedIn) {
          setAppSignInState((prevState) => ({
            ...prevState,
            pageStatus: PageStatusEnum.chooseStation,
          }));
          break;
        }
        // Otherwise only show the modal once we know there is nobody signed in
        if (!isLoadingProfile) {
          setSigninModalOpen(SignInModalStateEnum.True);
          sendPbsGtmEvent({
            feature_category: APP_SIGN_IN_FEATURE_CATEGORY,
            feature_name: "user sign in",
            object_action: "show sign in modal",
            pbs_platform: appSignInState.deviceType,
          });
        }
        break;
      case PageStatusEnum.chooseStation:
        const hasFavoriteStation = appSignInState.favoriteStation !== null;
        const currentStationID = Cookies.get(STATION_ID_COOKIE);
        switch(true) {
          // 1. Happy path - the user already has a favorite station;
          // We just move on to device registration
          case hasFavoriteStation:
            moveFromChooseStationToDeviceRegister(true);
            break;
          // 2. If the user doesn't have a favorite station, but is localized
          // anyway, we save the current station as their favorite
          // (which will then move the user on to device registration)
          case !hasFavoriteStation && currentStationID !== undefined:
            saveFavoriteStation(currentStationID);
            break;
          // 3. If the user doesn't have a favorite station and is not localized,
          // we open the change station modal to allow them to select one
          // (which will then save that station as a favorite,
          // and move the user on to device registration)
          case !hasFavoriteStation && currentStationID === undefined:
          default:
            sendPbsGtmEvent({
              feature_category: APP_SIGN_IN_FEATURE_CATEGORY,
              feature_name: "user localization",
              object_action: "show choose station modal",
              pbs_platform: appSignInState.deviceType,
            });
            openChangeStationModal(setChangeStationModalOpen);
            break;
        }
        break;
      case PageStatusEnum.deviceRegisterLoading:
        if (appSignInState.deviceType && appSignInState.authCode && appSignInState.uid) {
          // send a request to profile service to register a new device for the user
          const registerDevice = async () => {
            try {
              const response = await registerToUser(
                appSignInState.authCode as string,
                appSignInState.deviceType as string,
                appSignInState.uid as string
              );
              // errorCode 0 means success
              if (response.errorCode === 0) {
                const campaignInfoCode = getCampaignInfoCode(appSignInState.deviceType!);
                // if the deviceType matches a valid campaignInfoCode
                if (campaignInfoCode) {
                  // send a request to profile service to log a new campaignInfo code for this user
                  // (to track leads for marketing purposes)
                  putCampaignInfo(campaignInfoCode);
                }
                // progress to the success page!
                setAppSignInState((prevState) => ({
                  ...prevState,
                  pageStatus: PageStatusEnum.success
                }))
              } else {
                console.error(`Device registration failed: Error code ${response.errorCode} - ${response.errorMessage}`);
                setAppSignInState((prevState) => ({
                  ...prevState,
                  pageStatus: PageStatusEnum.error
                }))
              }
            } catch (error) {
              console.error(error);
              setAppSignInState((prevState) => ({
                ...prevState,
                pageStatus: PageStatusEnum.error
              }))
            }
          }

          registerDevice()
        } else {
          console.error(
            'Device registration skipped: missing required state',
            {
              hasDeviceType: !!appSignInState.deviceType,
              hasAuthCode: !!appSignInState.authCode,
              hasUid: !!appSignInState.uid,
            }
          );
          setAppSignInState((prevState) => ({
            ...prevState,
            pageStatus: PageStatusEnum.error
          }))
        }
        break;
      case PageStatusEnum.error:
        sendPbsGtmEvent({
          feature_category: APP_SIGN_IN_FEATURE_CATEGORY,
          object_action: "device activation error",
          pbs_platform: appSignInState.deviceType,
        });
        clientMetricLogger(
          APP_SIGN_IN_METRICS.NAMESPACE,
          APP_SIGN_IN_METRICS.FAILURE_ACTIVATION_ERROR,
        );
        break;
      case PageStatusEnum.success:
        // This check here is necessary otherwise we introduce an infinite loop
        // and get a Maximum Update Depth Exceeded error.
        if (appSignInState.resetOnNextLoad === false) {
          setAppSignInState((prevState) => ({...prevState, resetOnNextLoad: true}))
          sendPbsGtmEvent({
            feature_category: APP_SIGN_IN_FEATURE_CATEGORY,
            object_action: "device activation success",
            pbs_platform: appSignInState.deviceType,
          });
          clientMetricLogger(
            APP_SIGN_IN_METRICS.NAMESPACE,
            APP_SIGN_IN_METRICS.SUCCESS,
          );
        }
        break;
      default:
        console.log('AppSignInFlow: Unknown page status', appSignInState.pageStatus);
        setAppSignInState(defaultAppSignInState);
        break;
    }

    return () => {
      if (appSignInState.pageStatus === PageStatusEnum.signIn) {
        // Make sure we are not displaying the sign in modal when the component unmounts
        setSigninModalOpen(SignInModalStateEnum.False)
      }
    }
  }, [
    appSignInState,
    hasMounted,
    isLoadingProfile,
    isSignedIn,
    moveFromChooseStationToDeviceRegister,
    saveFavoriteStation,
    setAppSignInState,
    setChangeStationModalOpen,
    setSigninModalOpen,
    determineDeviceType
  ]);

  // Checks if component is mounted on the client before attempting to
  // hydrate, otherwise a hydration error occurs
  if (!hasMounted || isLoadingProfile ) {
    return null;
  }

  const isLoading = (
    (appSignInState.pageStatus === PageStatusEnum.authCodeSubmitLoading) ||
    (appSignInState.pageStatus === PageStatusEnum.deviceRegisterLoading)
  )

  const handleSignInModalLinkClick = (behavior?: string) => {
    sendPbsGtmEvent({
      feature_category: APP_SIGN_IN_FEATURE_CATEGORY,
      feature_name: "user sign in",
      object_action: "click link",
      object_action_behavior: `clicked ${behavior} link`,
      pbs_platform: appSignInState.deviceType,
    });
  }

  const handleSignInModalClose = () => {
    setSigninModalOpen(SignInModalStateEnum.False);
    sendPbsGtmEvent({
      feature_category: APP_SIGN_IN_FEATURE_CATEGORY,
      feature_name: "user sign in",
      object_action: "close sign in modal",
      pbs_platform: appSignInState.deviceType,
    });
    setAppSignInState((prevState) => ({...prevState, pageStatus: PageStatusEnum.authCodeInput}));
  }

  const handleChangeStationModalClose = () => {
    setChangeStationModalOpen(false);
    setAppSignInState((prevState) => ({...prevState, pageStatus: PageStatusEnum.authCodeInput}));
  }

  return (
    <>
      <FlowHeader
        stationLogo={appSignInState?.stationLogo || undefined}
        stationShortCommonName={appSignInState?.stationShortCommonName || undefined}
      />
      <div className={styles.app_sign_in_content}>
        {appSignInState.pageStatus === PageStatusEnum.authCodeInput && (
          <AuthCodeInput />
        )}

        {isLoading && (
          <LoadingSpinner
            className={styles.app_sign_in_loading}
          />
        )}

        {appSignInState.pageStatus === PageStatusEnum.confirmEmail && (
          <ConfirmEmail />
        )}

        {appSignInState.pageStatus === PageStatusEnum.success && (
          <AppSignInSuccess stationData={stationData} />
        )}

        {appSignInState.pageStatus === PageStatusEnum.error && (
          <AppSignInError />
        )}

        <SignInModal
          onClose={handleSignInModalClose}
          onLinkClick={handleSignInModalLinkClick}
        />
        <ChangeStationModal
          onClose={handleChangeStationModalClose}
          handleConfirmStationClick={handleConfirmStationClick}
        />

        {process.env.NODE_ENV === 'development' && (
          <pre>
              {JSON.stringify(appSignInState, null, 2)}
          </pre>
        )}
      </div>
    </>
  );
}
